// src/App.jsx
// FASE 1.C — Shell de la app: HelmetProvider + BrowserRouter + AuthProvider + Routes.
// El "App.jsx legacy" se renombró a src/pages/admin/AdminDashboard.jsx.
// Las 6 pantallas auth (FASE 1.B) se conectan acá.
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { AuthProvider } from './auth/AuthContext';
import InstallBanner from './components/InstallBanner';

// ── App móvil cliente (PWA) — src/app/ ──
import AppShell from './app/AppShell';
import AppHome from './app/v3/screens/Inicio';
import AppSignals from './app/v3/screens/Senales';
import AppHistory from './app/v3/screens/Historial';
import AppProfile from './app/screens/Profile';
import AppRangos from './app/screens/Rangos';
import AppPartidoDetalle from './app/v3/screens/Detalle';
import AppInstalar from './app/screens/Instalar';
// Handoff v3 · FASE 1 (pantallas nuevas; las existentes no cambian)
import AppOnboarding from './app/v3/screens/Onboarding';
import AppNotificaciones from './app/v3/screens/Notificaciones';
import AppMiCuenta from './app/v3/screens/MiCuenta';
import AppTelegram from './app/v3/screens/Telegram';
import AppAgenda from './app/v3/screens/Agenda';
import ProtectedRoute from './auth/ProtectedRoute';

// Públicas
import LandingC from './pages/public/LandingC';
import Signup from './pages/public/Signup';
import { PausedGate } from './pages/ServicePaused';
import PauseBanner from './components/PauseBanner';
import Login from './pages/public/Login';
import VerifyEmail from './pages/public/VerifyEmail';
import RecoverStep1 from './pages/public/RecoverStep1';
import RecoverEmailSent from './pages/public/RecoverEmailSent';
// FASE 3.6: RecoverStep2 y RecoverStep3 son showroom OTP sin lógica.
// El flujo real es /recuperar → email Resend → /auth/action?mode=resetPassword.
// Los archivos quedan en disk (sin import ni Route) por ahora.
import AuthAction from './pages/public/AuthAction';
import Terms from './pages/public/Terms';
import Privacy from './pages/public/Privacy';
import Support from './pages/public/Support';
import NotFound from './pages/public/NotFound';
import SinPermisos from './pages/public/SinPermisos';
import ComoFunciona from './pages/public/ComoFunciona';
import Aprende from './pages/public/Aprende';
import SenalesGratis from './pages/public/SenalesGratis';
import ActivateBeta from './pages/public/ActivateBeta';
import PromoBeta from './pages/public/PromoBeta';
import Oportunidades from './pages/client/Oportunidades';

// Pagos + Onboarding (FASE 3.A + 3.B + F5)
// Paywall.jsx se mantiene en el codebase para referencia / rollback rápido,
// pero no se renderiza por ruta — /paywall ahora redirige a /checkout.
import PaymentReturn from './pages/payments/PaymentReturn';
import SubscribeCard from './pages/payments/SubscribeCard';
// FASE 1 unificación: /checkout combina selección de plan + widget Flow en
// una sola vista (post-click el widget aparece en lugar del botón confirmar).
import Checkout from './pages/payments/Checkout';
import PaymentSuccess from './pages/payments/PaymentSuccess';
import TrialActive from './pages/payments/TrialActive';
import PaymentRejected from './pages/payments/PaymentRejected';
import ActivationPending from './pages/payments/ActivationPending';
import TelegramConnect from './pages/payments/TelegramConnect';

// Áreas autenticadas
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPanel from './pages/admin/AdminPanel';
import AdminProtected from './components/admin/AdminProtected';
import ResultadosTotales from './pages/admin/ResultadosTotales';
import ClientDashboard from './pages/client/ClientDashboard';

// FASE 2.A: la ruta `/` ahora es la landing pública (ya no redirige según rol).
// Usuarios logueados pueden ver la landing igual; el botón "Iniciar sesión"
// los lleva a /login que detecta sesión activa y redirige.

// Lista de rutas que activan el centrado vertical del card auth en
// desktop. Se aplica vía body.auth-page-active (ver mockup-desktop.css).
// Excluye '/' (Landing) y '/dashboard*' (full layout con sidebar).
const AUTH_ROUTE_PREFIXES = [
  '/login',
  '/signup',
  '/recuperar',
  '/verificar-email',
  '/auth/action',
  '/paywall',
  '/pago/',
  '/conectar-telegram',
  '/sin-permisos',
  '/terminos',
  '/privacidad',
  '/soporte',
];

// GA4 RouteTracker — manda pageview a Google Analytics en cada cambio de
// ruta de la SPA. Vive DENTRO del <BrowserRouter> para poder usar
// useLocation. La initialize global vive en index.js (una sola vez).
//
// PII: las rutas listadas en PII_SENSITIVE_PREFIXES llevan tokens en la
// query string (oobCode de Firebase auth, flowOrder/token de Flow, etc.)
// y SOLO mandamos el pathname para esas. El resto manda pathname + search,
// que aporta granularidad (ej. ?tab=signals) sin riesgo.
const PII_SENSITIVE_PREFIXES = [
  '/auth/action',         // Firebase verifyEmail/resetPassword → oobCode token
  '/pago/return',         // Flow callback → token, flowOrder
  '/conectar-telegram',   // posible code
  '/recuperar',           // confirm token en algunas variantes
];

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const isSensitive = PII_SENSITIVE_PREFIXES.some((p) =>
      location.pathname === p || location.pathname.startsWith(p + '/')
    );
    const page = isSensitive
      ? location.pathname
      : location.pathname + location.search;
    ReactGA.send({ hitType: 'pageview', page });
  }, [location.pathname, location.search]);
  return null;
}

function AuthPageClassManager() {
  const location = useLocation();
  useEffect(() => {
    const pathname = location.pathname;
    const isAuth = AUTH_ROUTE_PREFIXES.some((prefix) => {
      // Si el prefix termina en '/' (ej: '/pago/'), match exacto del
      // prefijo subpath (ej: '/pago/exito'). Caso contrario, exacto o
      // subpath con '/'.
      if (prefix.endsWith('/')) return pathname.startsWith(prefix);
      return pathname === prefix || pathname.startsWith(prefix + '/');
    });
    if (isAuth) {
      document.body.classList.add('auth-page-active');
    } else {
      document.body.classList.remove('auth-page-active');
    }
    return () => {
      document.body.classList.remove('auth-page-active');
    };
  }, [location.pathname]);
  return null;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <RouteTracker />
          <AuthPageClassManager />
          <InstallBanner />
          <PauseBanner />
          <Routes>
            <Route path="/" element={<LandingC />} />

            <Route path="/login" element={<Login />} />
            <Route
              path="/signup"
              element={(
                <PausedGate>
                  <Signup />
                </PausedGate>
              )}
            />
            <Route
              path="/verificar-email"
              element={
                <ProtectedRoute>
                  <VerifyEmail />
                </ProtectedRoute>
              }
            />
            <Route path="/recuperar" element={<RecoverStep1 />} />
            <Route path="/recuperar/enviado" element={<RecoverEmailSent />} />
            {/* FASE 3.6: action handler para verifyEmail / resetPassword
                (links generados por Cloud Functions y enviados via Resend) */}
            <Route path="/auth/action" element={<AuthAction />} />

            <Route
              path="/paywall"
              element={<Navigate to="/checkout" replace />}
            />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pago/return"
              element={
                <ProtectedRoute>
                  <PaymentReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pago/suscribir"
              element={
                <ProtectedRoute>
                  <SubscribeCard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pago/exito"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trial-activo"
              element={
                <ProtectedRoute>
                  <TrialActive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pago/error"
              element={
                <ProtectedRoute>
                  <PaymentRejected />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pago/pendiente"
              element={
                <ProtectedRoute>
                  <ActivationPending />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conectar-telegram"
              element={
                <ProtectedRoute>
                  <TelegramConnect />
                </ProtectedRoute>
              }
            />

            <Route path="/terminos" element={<Terms />} />
            <Route path="/privacidad" element={<Privacy />} />
            <Route path="/soporte" element={<Support />} />
            {/* Landing pública de captación Meta Ads → Telegram free. */}
            <Route path="/senales-gratis" element={<SenalesGratis />} />
            {/* Beta gratuita FREE: canje del token de email + landing de captación. */}
            <Route
              path="/activate-beta"
              element={(
                <PausedGate>
                  <ActivateBeta />
                </PausedGate>
              )}
            />
            <Route
              path="/promo/beta"
              element={(
                <PausedGate>
                  <PromoBeta />
                </PausedGate>
              )}
            />
            {/* UX audit ISSUE 5: target del redirect cuando falta rol admin */}
            <Route path="/sin-permisos" element={<SinPermisos />} />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requireRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Panel de gestión interno. Sin entrada en sidebar/footer/sitemap.
                AdminProtected: si no es admin renderiza 404 disimulado. */}
            <Route
              path="/gestion"
              element={
                <AdminProtected>
                  <AdminPanel />
                </AdminProtected>
              }
            />

            <Route
              path="/resultados-totales"
              element={
                <ProtectedRoute requireRole="admin">
                  <ResultadosTotales />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireAccess>
                  <ClientDashboard initialPage="live" />
                </ProtectedRoute>
              }
            />

            {/* ── App móvil cliente (PWA) ── */}
            <Route path="/app" element={<AppShell />}>
              <Route index element={<AppHome />} />
              <Route path="senales" element={<AppSignals />} />
              <Route path="historial" element={<AppHistory />} />
              <Route path="perfil" element={<AppProfile />} />
              <Route path="rangos" element={<AppRangos />} />
              <Route path="partido/:id" element={<AppPartidoDetalle />} />
              {/* Handoff v3 · FASE 1. registro/recuperar se renderizan en
                  AppShell (pre-sesión); acá solo declaran la ruta. */}
              <Route path="registro" element={null} />
              <Route path="recuperar" element={null} />
              <Route path="recuperar/enviado" element={null} />
              <Route path="bienvenida" element={<AppOnboarding />} />
              <Route path="notificaciones" element={<AppNotificaciones />} />
              <Route path="cuenta" element={<AppMiCuenta />} />
              <Route path="telegram" element={<AppTelegram />} />
              <Route path="agenda" element={<AppAgenda />} />
            </Route>
            <Route path="/app/instalar" element={<AppInstalar />} />
            <Route path="/app-instalar" element={<AppInstalar />} />

            {/* UX audit ISSUE C: deeplink al historial (antes era tab interno). */}
            <Route
              path="/historial"
              element={
                <ProtectedRoute requireAccess>
                  <ClientDashboard initialPage="signals" />
                </ProtectedRoute>
              }
            />

            <Route
              path="/como-funciona"
              element={
                <ProtectedRoute requireAccess>
                  <ComoFunciona />
                </ProtectedRoute>
              }
            />

            {/* /recomendaciones se eliminó: reemplazada por /aprende.
                Mantenemos redirect para links guardados/compartidos. */}
            <Route
              path="/recomendaciones"
              element={<Navigate to="/aprende" replace />}
            />

            <Route
              path="/aprende"
              element={
                <ProtectedRoute requireAccess>
                  <Aprende />
                </ProtectedRoute>
              }
            />

            <Route
              path="/oportunidades"
              element={
                <ProtectedRoute requireAccess>
                  <Oportunidades />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
